import type { FC } from "react"
import { useState } from "react"

import { AnimatePresence, motion } from "framer-motion"
import { useRecoilValue } from "recoil"
import styled from "styled-components"

import { proposalRemoteState } from "~/containers/Proposal/state/proposal/proposal-state"
import {
  currentPermissionsState,
  currentUserState,
} from "~/containers/Proposal/state/user/user-state"
import type { Design } from "~/graphql/client"
import { canSelfDesign } from "~/utils/canManipulateDesign"

import { ReactComponent as CopyIcon } from "@svg/copy.svg"
import { ReactComponent as CommentIcon } from "@svg/new-message.svg"
import { ReactComponent as PencilIcon } from "@svg/pencil-2.svg"
import { ReactComponent as TrashIcon } from "@svg/trash-icon.svg"

import { AddComment } from "./AddComment"
import { Cancel } from "./Cancel"
import { Duplicate } from "./Duplicate"
import { EditInfo } from "./EditInfo"
import { useDesignerCapabilities } from "../../designer-capabilities"

const Wrapper = styled(motion.div)`
  position: absolute;
  bottom: 30px;
  left: 30px;
  border-radius: 7px;
  background-color: white;
  padding: var(--space-s);
  display: flex;
  gap: var(--space-xs);
  align-items: center;
  min-width: 350px;
  z-index: 1;
`
const IconButton = styled.button<{ isActive?: boolean }>`
  border: 1px solid #d6e1f0;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 45px;
  height: 45px;
  background-color: ${({ isActive }) => (isActive ? `#D6E1F0` : `white`)};
  transition:
    background-color 0.5s ease,
    color 0.5s ease;
  svg {
    fill: var(--grey-dark);
  }
  :disabled {
    cursor: not-allowed;
  }
`
const RequestButton = styled(IconButton)<{
  isCurrent: boolean
}>`
  border: 1px solid var(--blue);
  color: ${({ isCurrent }) => (isCurrent ? `var(--blue)` : `white`)};
  background-color: ${({ isCurrent }) => (isCurrent ? `white` : `var(--blue)`)};
  width: 150px;
  flex-grow: 1;
  text-transform: capitalize;
  font-size: var(--step--2);
  cursor: ${({ isCurrent }) => (!isCurrent ? `pointer` : `not-allowed`)};
`

export const FloatingBar: FC<{
  activateDesign: () => void
  isCurrent: boolean
  designRevision?: Design
  disabled?: boolean
}> = ({ activateDesign, isCurrent, designRevision, disabled }) => {
  const [active, setActive] = useState<
    `cancel` | `comment` | `duplicate` | `edit` | null
  >(null)
  const currentPermissions = useRecoilValue(currentPermissionsState)
  const canDuplicate = canSelfDesign(currentPermissions)
  const currentUser = useRecoilValue(currentUserState)
  const proposal = useRecoilValue(proposalRemoteState)
  const { seller } = proposal
  const isCurrentUserProposalSeller = currentUser?.id === seller?.id
  const close = () => setActive(null)
  const { editInfo } = useDesignerCapabilities()

  return (
    <Wrapper
      initial={{ opacity: 0, x: 0, y: `100%` }}
      animate={{ opacity: 1, x: 0, y: 0, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, x: 0, y: `100%`, transition: { duration: 0.25 } }}
    >
      {canDuplicate && (
        <IconButton
          title="Duplicate Design"
          onClick={() => setActive(active === `duplicate` ? null : `duplicate`)}
          isActive={active === `duplicate`}
        >
          <CopyIcon />
        </IconButton>
      )}
      <AnimatePresence>
        {active === `duplicate` && (
          <Duplicate
            close={close}
            designRevision={designRevision}
            disabled={disabled}
          />
        )}
      </AnimatePresence>

      {editInfo && (
        <>
          <IconButton
            title="Edit Design Info"
            onClick={() => setActive(active === `edit` ? null : `edit`)}
            isActive={active === `edit`}
          >
            <PencilIcon />
          </IconButton>
          <AnimatePresence>
            {active === `edit` && <EditInfo close={close} />}
          </AnimatePresence>
        </>
      )}

      <IconButton
        title="Add Comment"
        onClick={() => setActive(active === `comment` ? null : `comment`)}
        isActive={active === `comment`}
      >
        <CommentIcon />
      </IconButton>
      <AnimatePresence>
        {active === `comment` && (
          <AddComment close={close} design={designRevision} />
        )}
      </AnimatePresence>

      {isCurrentUserProposalSeller && (
        <IconButton
          title="Cancel Request"
          onClick={() => setActive(active === `cancel` ? null : `cancel`)}
          isActive={active === `cancel`}
        >
          <TrashIcon />
        </IconButton>
      )}
      <AnimatePresence>
        {active === `cancel` && (
          <Cancel
            close={close}
            designRevision={designRevision}
            isCurrent={isCurrent}
          />
        )}
      </AnimatePresence>

      <RequestButton
        onClick={() => !isCurrent && activateDesign()}
        isCurrent={isCurrent}
        disabled={isCurrent}
      >
        {isCurrent ? `Current Design` : `Set As Current`}
      </RequestButton>
    </Wrapper>
  )
}
